import { useState } from "react";
import { Star, Plus, Minus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Reveal from "../components/Reveal";
import Icon from "../components/Icon";
import IngredientGroups from "../components/IngredientGroups";
import { useCart } from "../context/CartContext";
import { PRODUCT, formatPrice } from "../data/catalog";
import { BENEFITS, TRUST_BADGES } from "../data/content";
import { TOTAL_ACTIVES, TOTAL_SYSTEMS } from "../data/ingredients";

const Product = () => {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [plan, setPlan] = useState("subscribe");
  const subscribePrice = PRODUCT.subscribePrice;

  const handleAdd = () => {
    addItem(PRODUCT.id, plan, qty);
    toast.success(plan === "subscribe" ? "Subscription added to cart" : "Added to cart");
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* IMAGE */}
          <Reveal>
            <div className="lg:sticky lg:top-28">
              <div className="relative rounded-3xl bg-gradient-to-br from-sky-50 via-white to-white border border-slate-100 flex items-center justify-center aspect-square overflow-hidden">
                <div className="absolute inset-0 soft-orb opacity-80" />
                <span className="absolute top-5 left-5 z-10 text-[10px] font-bold tracking-widest uppercase bg-white/80 backdrop-blur px-3 py-1.5 rounded-full text-royal">
                  Premium Daily Wellness
                </span>
                <img
                  src="/images/brand/sachet_square.png"
                  alt="NARA Nutrition sachet"
                  className="relative w-full h-full object-contain p-10 drop-shadow-2xl"
                  data-testid="product-main-image"
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3" data-testid="product-stats">
                {[[`${TOTAL_ACTIVES}+`, "Active Ingredients"], [`${TOTAL_SYSTEMS}`, "Systems Supported"], ["100%", "Transparent"]].map(([v, l]) => (
                  <div key={l} className="rounded-2xl bg-white border border-slate-100 p-4 text-center">
                    <p className="font-heading text-2xl font-bold text-navy-900">{v}</p>
                    <p className="text-[11px] text-slate-400 mt-1 leading-tight">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* DETAILS */}
          <Reveal delay={0.1}>
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-royal mb-3 block">Daily Wellness</span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-navy-900 leading-tight">{PRODUCT.name}</h1>
            <div className="mt-4 flex items-center gap-3">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
              <span className="text-sm text-slate-500">4.8 · 2,500+ reviews</span>
            </div>
            <p className="mt-5 text-slate-500 leading-relaxed">{PRODUCT.description}</p>

            <div className="mt-6 flex flex-wrap gap-2">
              {BENEFITS.slice(0, 5).map((b) => (
                <span key={b.title} className="inline-flex items-center gap-1.5 text-xs font-medium text-navy-900 bg-sky-50 rounded-full px-3 py-1.5">
                  <Icon name={b.icon} className="w-3.5 h-3.5 text-royal" /> {b.title}
                </span>
              ))}
            </div>

            <div className="mt-8 space-y-3" data-testid="purchase-options">
              <button
                onClick={() => setPlan("subscribe")}
                data-testid="plan-subscribe"
                className={`w-full text-left rounded-2xl border-2 px-5 py-4 flex items-center justify-between transition-all ${plan === "subscribe" ? "border-royal bg-sky-50/50" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${plan === "subscribe" ? "border-royal" : "border-slate-300"}`}>
                    {plan === "subscribe" && <span className="w-2.5 h-2.5 rounded-full bg-royal" />}
                  </span>
                  <div>
                    <p className="font-semibold text-navy-900 text-sm">Subscribe & Save 15%</p>
                    <p className="text-xs text-slate-400">Delivered monthly · cancel anytime</p>
                  </div>
                </div>
                <span className="font-heading font-bold text-navy-900">{formatPrice(subscribePrice)}</span>
              </button>
              <button
                onClick={() => setPlan("once")}
                data-testid="plan-once"
                className={`w-full text-left rounded-2xl border-2 px-5 py-4 flex items-center justify-between transition-all ${plan === "once" ? "border-royal bg-sky-50/50" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${plan === "once" ? "border-royal" : "border-slate-300"}`}>
                    {plan === "once" && <span className="w-2.5 h-2.5 rounded-full bg-royal" />}
                  </span>
                  <div>
                    <p className="font-semibold text-navy-900 text-sm">One-Time Purchase</p>
                    <p className="text-xs text-slate-400">No subscription</p>
                  </div>
                </div>
                <span className="font-heading font-bold text-navy-900">{formatPrice(PRODUCT.price)}</span>
              </button>
            </div>

            <div className="mt-7 flex items-center gap-4">
              <div className="flex items-center border border-slate-200 rounded-full">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec" className="p-3"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center font-medium" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} data-testid="qty-inc" className="p-3"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                onClick={handleAdd}
                data-testid="add-to-cart-button"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-royal text-white rounded-full px-8 py-4 font-medium hover:bg-royal-600 transition-colors"
              >
                <ShoppingBag className="w-5 h-5" /> Add to Cart — {formatPrice((plan === "subscribe" ? subscribePrice : PRODUCT.price) * qty)}
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-400 text-center">{formatPrice(PRODUCT.perServing)} / serving · Free EU shipping over €75</p>

            <div className="mt-9 grid grid-cols-2 sm:grid-cols-3 gap-4 pt-8 border-t border-slate-100">
              {TRUST_BADGES.map((t) => (
                <div key={t.title} className="flex items-start gap-2.5">
                  <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-sky-50 text-royal shrink-0">
                    <Icon name={t.icon} className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-navy-900 leading-tight">{t.title}</p>
                    <p className="text-[11px] text-slate-400 leading-tight mt-0.5">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      {/* INGREDIENTS SUMMARY */}
      <section className="bg-[#FAFBFD] border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-royal mb-4 block">Full Transparency</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-navy-900">Inside every sachet.</h2>
            <p className="mt-5 text-slate-500 leading-relaxed">
              {TOTAL_ACTIVES} clinically-dosed active ingredients working across {TOTAL_SYSTEMS} systems. No proprietary blends —
              every milligram fully disclosed. Tap each system to explore.
            </p>
          </Reveal>
          <IngredientGroups defaultOpen={0} />
        </div>
      </section>
    </div>
  );
};

export default Product;
